import React, { useEffect, useState } from "react";
import { FaWhatsapp } from "react-icons/fa";
import { FiPhoneCall } from "react-icons/fi";
import { GiLotus } from "react-icons/gi";
import SEO from "../components/SEO";
import SectionTitle from "../components/SectionTitle";
import ServiceCard from "../components/ServiceCard";
import PageHero from "../components/PageHero";
import Loader from "../components/Loader";
import EmptyState from "../components/EmptyState";
import { SAURABH } from "../utils/siteData";
import { useServices } from "../hooks/useServices";
import { buildWhatsAppLink } from "../utils/whatsapp";
import api from "../utils/api";

const STEPS = [
  { step: "1", title: "Contact Us", desc: "Call or WhatsApp with your requirement and preferred date." },
  { step: "2", title: "Confirm Muhurat", desc: "We help finalize an auspicious date and time for your ceremony." },
  { step: "3", title: "Material Arrangement", desc: "We guide you on, or arrange, all required puja samagri." },
  { step: "4", title: "Pooja Performed", desc: "Saurabh Shastri arrives on time and performs the complete ritual with devotion." },
];

export default function SaurabhServices() {
  const { poojaServices, loading } = useServices("pooja");
  const [otherServices, setOtherServices] = useState([]);
  const [loadingOther, setLoadingOther] = useState(true);

  useEffect(() => {
    api
      .get("/other-services")
      .then((res) => setOtherServices(res.data))
      .catch(() => setOtherServices([]))
      .finally(() => setLoadingOther(false));
  }, []);

  const enquire = (serviceTitle) => {
    const link = buildWhatsAppLink(
      SAURABH.whatsapp,
      `Namaste 🙏, I would like to enquire about *${serviceTitle}*. Please share more details.`
    );
    window.open(link, "_blank");
  };

  const bookLink = buildWhatsAppLink(SAURABH.whatsapp, "Namaste 🙏, I would like to book a pooja service.");

  return (
    <div>
      <SEO
        title="Pooja Services by Saurabh Shastri"
        description="Pandit Saurabh Shastri offers Satyanarayan Mahapuja, Vastu Shanti, Ganesh Poojan, Gruhpravesh Vidhi, Namkaran Sanskar, Vivah Sanskar, Navchandi Yag, Navgraha Shanti and other religious poojas in Thane, Mumbai."
        keywords="satyanarayan puja thane, vastu shanti thane, gruhpravesh thane, ganesh poojan thane, vivah sanskar pandit thane, navchandi yag, navgraha shanti pooja"
        path="/pooja-services"
      />

      <PageHero
        eyebrow="Saurabh Shastri"
        title="Vedic Pooja Services"
        subtitle="Authentic, Shastra-sanctioned rituals performed with complete devotion - for every important occasion in your life."
      >
        <div className="flex justify-center gap-4">
          <a href={`tel:+91${SAURABH.phone}`} className="glass-card inline-flex items-center gap-2 text-white px-6 py-2.5 rounded-full font-semibold transition-colors">
            <FiPhoneCall /> {SAURABH.phoneDisplay}
          </a>
          <a
            href={bookLink}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 px-6 py-2.5 rounded-full font-semibold shadow-premium-lg transition-colors"
          >
            <FaWhatsapp /> WhatsApp
          </a>
        </div>
      </PageHero>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <SectionTitle eyebrow="Core Services" title="Primary Pooja & Sanskar Services" subtitle="Each ceremony is performed exactly as prescribed in the Shastras, with clear guidance throughout." />
          {loading ? (
            <Loader />
          ) : poojaServices.length === 0 ? (
            <EmptyState title="No services yet" subtitle="Please check back soon." />
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {poojaServices.map((s, i) => (
                <ServiceCard key={s.slug} title={s.title} desc={s.desc} image={s.image} detailLink={s.detailPath} index={i} onEnquire={() => enquire(s.title)} />
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="py-20 bg-gradient-to-b from-saffron-50 to-white">
        <div className="max-w-6xl mx-auto px-4">
          <SectionTitle eyebrow="Also Available" title="Other Religious Poojas & Sanskars" subtitle="Beyond the core services, Saurabh Shastri also performs the following ceremonies on request." />
          {loadingOther ? (
            <Loader />
          ) : otherServices.length === 0 ? (
            <EmptyState title="No additional services listed yet" subtitle="Please check back soon." />
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {otherServices.map((item) => (
                <button
                  key={item._id}
                  onClick={() => enquire(item.title)}
                  className="group text-left bg-white rounded-2xl border border-gold-100 p-6 shadow-sm hover:shadow-gold hover:-translate-y-1 transition-all"
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-saffron-100 to-gold-50 flex items-center justify-center text-saffron-600 shadow-inner mb-4 group-hover:scale-110 transition-transform">
                    <GiLotus size={24} />
                  </div>
                  <h4 className="font-heading text-maroon-800 text-sm font-semibold leading-snug mb-1.5 group-hover:text-saffron-700 transition-colors">
                    {item.title}
                  </h4>
                  {item.desc && (
                    <p className="text-xs text-gray-500 leading-relaxed line-clamp-2 mb-4">{item.desc}</p>
                  )}
                  <div className="flex items-center gap-1.5 text-xs font-medium text-saffron-600 opacity-0 group-hover:opacity-100 transition-opacity">
                    <FaWhatsapp size={14} /> Enquire Now
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4">
          <SectionTitle eyebrow="How It Works" title="Booking a Pooja is Simple" />
          <div className="relative grid sm:grid-cols-4 gap-8">
            {/* connecting line */}
            <div className="hidden sm:block absolute top-6 left-[12.5%] right-[12.5%] h-0.5 bg-gradient-to-r from-gold-200 via-saffron-400 to-gold-200" />
            {STEPS.map((s) => (
              <div key={s.step} className="relative text-center">
                <div className="w-12 h-12 rounded-full bg-maroon-gradient text-gold-200 flex items-center justify-center font-heading text-lg mx-auto mb-4 shadow-gold ring-4 ring-white relative z-10">
                  {s.step}
                </div>
                <h4 className="font-heading text-maroon-800 mb-1">{s.title}</h4>
                <p className="text-sm text-gray-600">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative py-16 bg-hero-gradient bg-mandala text-white text-center overflow-hidden">
        <div className="absolute inset-0 bg-gold-radial pointer-events-none" />
        <div className="relative max-w-3xl mx-auto px-4">
          <h3 className="font-heading text-3xl mb-4">Ready to Book Your Pooja?</h3>
          <a
            href={bookLink}
            target="_blank"
            rel="noreferrer"
            className="btn-gold inline-flex items-center gap-2 px-7 py-3 rounded-full font-semibold"
          >
            <FaWhatsapp /> Book on WhatsApp
          </a>
        </div>
      </section>
    </div>
  );
}
